import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { ActionCards } from 'components/actions/ActionCards'
import { useLPActions } from 'components/actions/useLPActions'
import { Block } from 'components/Block'
import PlanPerks from 'components/content/planPerks/PlanPerks'
import { DigitalProtectionLPSection } from 'components/cornerstone/digitalProtection/DigitalProtectionLPSection'
import { HeroWithPerks } from 'components/cornerstone/heroWithPerks/HeroWithPerks'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Icon, IconName } from 'components/icon'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { SecuritySection } from 'components/security/SecuritySection'
import { Signup, ToneContext } from 'components/signup/Signup'
import { Spacer } from 'components/spacer/Spacer'
import { Statistics } from 'components/statistics/Statistics'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { mutateAnalyticsData } from 'utils/analytics'
import { REVALIDATE_SECS } from 'utils/constants'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { getContentfulImages, getContentfulVideoIds } from 'utils/contentful/getPageImages'
import { numberRange } from 'utils/general'
import { useWindowSize } from 'utils/hooks'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'
import { NumberRange } from 'utils/types'
import CyberscoutLogo from 'images/extra-svg/CyberscoutLogoThemedSection.svg'
import GoodtrustLogo from 'images/extra-svg/LogoRelease.svg'

type Props = {
  faq: ContentfulFAQ
  mediaMentions: ContentfulMediaMentions
}

const statisticIcons: Record<NumberRange<3>, IconName> = {
  '1': 'Bug',
  '2': 'IdentityBadge',
  '3': 'Skull',
}
export default function Page(props: Props) {
  const { t } = useTranslation('digital_protection')
  useEffect(() => {
    mutateAnalyticsData({ type: 'digital_protection' })
  }, [])

  const images = useContentfulImages()
  const { isDesktop } = useWindowSize({ isDesktop: true })
  const actions = useLPActions([
    'secure_accounts',
    'secure_documents',
    'secure_devices',
    'cyberscout',
    'dashlane',
  ])

  const heroPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `digital_protection.hero.perk_${i}` as const)
  )

  const signupPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `digital_protection.section_signup.perk_${i}` as const)
  )

  return (
    <Layout
      header={<TopNavigationLandingPage variant="full" noSpacingBelow />}
      footer={<Footer variant="full" />}
    >
      <PageTitle title={t('digital_protection.meta_title')} />
      <PageMeta
        description={t('digital_protection.meta_description')}
        image="/images/preview/digital-protection.png"
      />

      <HeroWithPerks
        image={isDesktop ? images.digital_protection_hero : images.digital_protection_hero_mobile}
        mentions={props.mediaMentions}
        buttonText={t('digital_protection.hero.cta')}
        mentionsCaption={t('digital_protection.hero.mentions_caption')}
        title={t('digital_protection.hero.title')}
        perks={heroPerks}
        bgColor="gray"
        videoButton="digital_protection"
      />

      <Spacer size={[2, 6]} />
      <Block>
        <ActionCards
          title={t('digital_protection.action_cards_title')}
          actions={actions}
          variant="text-button"
          scrollability="mobile-only"
        />
      </Block>

      <Spacer size={[3, 6]} />

      <Statistics
        title={t('digital_protection.statistics.title')}
        items={numberRange(3).map((i) => ({
          title: t(`digital_protection.statistics.item_${i}.title`),
          description: t(`digital_protection.statistics.item_${i}.desc`),
          icon: statisticIcons[i],
        }))}
      />

      <Spacer size={[3, 6]} />

      <DigitalProtectionLPSection
        section="section_cyberscout"
        ctaIcon="Cyberscout"
        ctaHref="/cyberscout-coverage"
        extraProps={{ logoAboveTitle: <CyberscoutLogo /> }}
      />

      <DigitalProtectionLPSection
        section="section_dashlane"
        ctaIcon="Dashlane"
        ctaHref="/dashlane-password-manager"
        extraProps={{
          logoAboveTitle: <Icon name="ColorDashlane" width="6rem" height="2rem" />,
          imagePlacement: 'right',
        }}
      />

      <DigitalProtectionLPSection
        section="section_digital_vault"
        ctaIcon="Goodtrust"
        ctaHref="/digital-vault"
        extraProps={{ logoAboveTitle: <GoodtrustLogo /> }}
      />

      <PlanPerks />
      <Spacer size={[3, 6]} />

      <SecuritySection />

      <Spacer size={[3, 6]} />
      <FaqTwoColumns content={props.faq} />
      <Spacer size={[3, 6]} />
      <ToneContext.Provider value="dark">
        <Signup
          caption={t('digital_protection.section_signup.caption')}
          title={t('digital_protection.section_signup.title')}
          perks={signupPerks}
        />
      </ToneContext.Provider>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const mediaMentions = await getContentfulMediaMentions('global')
  const faq = await getContentfulFAQ('digital-protection')

  return {
    props: {
      ...(await getContentfulImages()),
      ...(await getContentfulVideoIds()),
      ...(await getIntlProps(ctx, ['digital_protection', 'pricing_plan'])),
      faq,
      mediaMentions,
    },
    revalidate: REVALIDATE_SECS,
  }
}
